import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable, runInAction} from "mobx";
import {isNumber} from "lodash";
import {Bindings} from "data/constants/bindings";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {ITournament} from "data/providers/json/json.provider";
import type {ISquadsStore} from "data/stores/squads/squds.store";
import type {IUserStore} from "data/stores/user/user.store";
import {PickemWidgetType, RequestState} from "data/enums";
import {createFanHubEvent} from "data/utils/helpers";

export interface IPicksController extends ViewController {
	get tournaments(): ITournament[];
	get isAuthorized(): boolean;
	get isLoading(): boolean;
}

@injectable()
export class PicksController implements IPicksController {
	@observable private _requestState = RequestState.PENDING;

	get tournaments(): ITournament[] {
		return this._roundsStore.tournamentsBySelectedRound;
	}

	get isAuthorized(): boolean {
		return this._userStore.isAuthorized;
	}

	get isLoading() {
		return this._requestState === RequestState.PENDING;
	}

	constructor(
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	dispose(): void {
		return;
	}

	init(): void {
		this.fetchRounds();

		createFanHubEvent("d95ee7d9-62fb-4b6d-950d-1b0c35d842d4");
	}

	private fetchRounds(): void {
		void this._roundsStore
			.fetchRounds()
			.then(() => {
				this._roundsStore.setSelectedRound(this._roundsStore.currentRoundForPrediction);
				this.fetchPredictionsAnswers();

				runInAction(() => {
					this._requestState = RequestState.SUCCESS;
				});
			})
			.catch(() => {
				runInAction(() => {
					this._requestState = RequestState.ERROR;
				});
			});
	}

	private fetchPredictionsAnswers(): void {
		const round = this._roundsStore.currentSelectedRound;
		const roundId = round?.id;

		if (roundId) {
			if (this._userStore.isAuthorized) {
				void this._predictionsStore.fetchPredictionsAnswers(roundId).then(() => {
					const queryParams = new URLSearchParams(window.location.search);
					const tournamentId = Number(queryParams.get("tournamentId"));
					const squadId = Number(queryParams.get("squadId"));
					const type = queryParams.get("type");

					if (
						isNumber(squadId) &&
						isNumber(tournamentId) &&
						type === PickemWidgetType.Pick
					) {
						this._predictionsStore.updatePredictions({
							tournament: tournamentId,
							squad: squadId,
						});
					}
				});
			}

			void this._predictionsStore.fetchPredictionsTips(roundId).then(() => {
				this._predictionsStore.savePredictionsTemporaryAnswers();
			});
			void this._squadsStore.fetchSquads();
		}
	}
}
